import { ClientItem } from "components/Modals/Client/types"
import { parseISOString } from "utils/string-localization-helper"
import { Client } from "./client"
import getConfig from "next/config"

const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

export const enum QuestionDisplayType {
    // ADDRESS = "ADDRESS", uncommment out when used
    CHECKBOX = "CHECKBOX",
    // DATE_TIME = "DATE_TIME", uncommment out when used
    DROPDOWN = "DROPDOWN",
    FILE = "FILE",
    RADIO_BUTTON = "RADIO_BUTTON",
    TEXT = "TEXT",
    YES_NO = "YES_NO"
}

export const enum QuestionType {
    MULTI_SELECT = "MULTI_SELECT",
    SINGLE_SELECT = "SINGLE_SELECT",
    TEXT = "TEXT"
}

export type IntakeAnswerDTO = {
    answer: string
    answerNumber: number
    id?: string
    intakeQuestionId?: string
}

export type IntakeAnswer = {
    answer: string
    answer_number: number
    id?: string
    intake_question_id?: string
    intake_client_id?: string
}

export function mapIntakeAnswerToDTO(intake_answer: IntakeAnswer): IntakeAnswerDTO {
    return {
        answer: intake_answer.answer,
        answerNumber: intake_answer.answer_number,
        id: intake_answer.id,
        intakeQuestionId: intake_answer.intake_question_id
    } as IntakeAnswerDTO
}

export function mapIntakeAnswerFromDTO(intake_answer: IntakeAnswerDTO, index: number): IntakeAnswer {
    return {
        answer: intake_answer.answer,
        answer_number: intake_answer.answerNumber,
        id: intake_answer.id,
        intake_question_id: intake_answer.intakeQuestionId
    } as IntakeAnswer
}

export type IntakeQuestionDTO = {
    answers?: IntakeAnswerDTO[]
    askedOnce: boolean
    askedWithCouponCode: string
    displayType: QuestionDisplayType
    id?: string
    intakeFormId?: string
    internal: boolean
    question: string
    questionNumber: number
    questionType: QuestionType
    required: boolean
}

export type IntakeQuestion = {
    answers?: IntakeAnswer[]
    asked_once: boolean
    asked_with_coupon_code: string
    display_type: QuestionDisplayType
    id?: string
    intake_form_id?: string
    internal: boolean
    question: string
    question_number: number
    question_type: QuestionType
    required: boolean
}

export function mapIntakeQuestionToDTO(intake_question: IntakeQuestion, index: number): IntakeQuestionDTO {
    let answers: IntakeAnswerDTO[] | undefined = intake_question.display_type == QuestionDisplayType.YES_NO && intake_question.answers?.length == 0 ? [{ answer: "yes", answerNumber: 0 }, { answer: "no", answerNumber: 1 }] : intake_question.answers?.map(mapIntakeAnswerToDTO)
    return {
        answers,
        askedOnce: intake_question.asked_once,
        askedWithCouponCode: intake_question.asked_with_coupon_code,
        displayType: intake_question.display_type,
        id: intake_question.id,
        intakeFormId: intake_question.intake_form_id,
        internal: intake_question.internal,
        question: intake_question.question,
        questionNumber: index,
        questionType: intake_question.question_type,
        required: intake_question.required
    } as IntakeQuestionDTO
}

export function mapIntakeQuestionFromDTO(intake_question: IntakeQuestionDTO): IntakeQuestion {
    return {
        answers: intake_question.answers?.map(mapIntakeAnswerFromDTO),
        asked_once: intake_question.askedOnce,
        asked_with_coupon_code: intake_question.askedWithCouponCode,
        display_type: intake_question.displayType,
        id: intake_question.id,
        intake_form_id: intake_question.intakeFormId,
        internal: intake_question.internal,
        question: intake_question.question,
        question_number: intake_question.questionNumber,
        question_type: intake_question.questionType,
        required: intake_question.required
    } as IntakeQuestion
}

export type IntakeFormDTO = {
    categoryIds?: string[]
    createdAt?: string
    description?: string
    id?: string
    locationIds?: string[]
    name: string // form name, tenant side
    questions?: IntakeQuestionDTO[]
    responseCount?: number
    serviceIds?: string[]
    title: string
    tenantId: string
}

export type IntakeForm = {
    category_ids?: string[]
    created_at?: Date
    description?: string
    id?: string
    location_ids?: string[]
    name: string
    questions?: IntakeQuestion[]
    response_count?: number
    service_ids?: string[]
    title: string
}

export function mapIntakeFormFromDTO(intake_form: IntakeFormDTO): IntakeForm {
    return {
        category_ids: intake_form.categoryIds,
        created_at: intake_form.createdAt ? parseISOString(intake_form.createdAt) : undefined,
        description: intake_form.description,
        id: intake_form.id,
        location_ids: intake_form.locationIds,
        name: intake_form.name,
        questions: intake_form.questions?.map((question) => mapIntakeQuestionFromDTO(question))?.sort((a, b) => a.question_number - b.question_number) || undefined,
        response_count: intake_form.responseCount,
        service_ids: intake_form.serviceIds,
        title: intake_form.title
    } as IntakeForm
}

export function mapIntakeFormToDTO(intake_form: IntakeForm, tenant_id: string): IntakeFormDTO {
    return {
        categoryIds: intake_form.category_ids,
        description: intake_form.description,
        id: intake_form.id,
        locationIds: intake_form.location_ids,
        name: intake_form.name,
        questions: intake_form.questions?.map((question, index) => mapIntakeQuestionToDTO(question, index)) || [],
        serviceIds: intake_form.service_ids,
        title: intake_form.title,
        tenantId: tenant_id
    }
}

export type DraggableQuestionType = {
    name: string
    img_url: string
    display_type: QuestionDisplayType
    type: QuestionType
}

export const QUESTION_TYPE_LIST: DraggableQuestionType[] = [
    {
        name: "forms.short_textbox",
        img_url: "https://static.aot.plus/images/question-types/question-short-text.svg",
        display_type: QuestionDisplayType.TEXT,
        type: QuestionType.TEXT
    },
    {
        name: "forms.yes_no",
        img_url: "https://static.aot.plus/images/question-types/question-yes-no.svg",
        display_type: QuestionDisplayType.YES_NO,
        type: QuestionType.SINGLE_SELECT
    },
    {
        name: "forms.radio_button",
        img_url: "https://static.aot.plus/images/question-types/question-radio.svg",
        display_type: QuestionDisplayType.RADIO_BUTTON,
        type: QuestionType.SINGLE_SELECT
    },
    {
        name: "forms.dropdown",
        img_url: "https://static.aot.plus/images/question-types/question-dropdown.svg",
        display_type: QuestionDisplayType.DROPDOWN,
        type: QuestionType.SINGLE_SELECT
    },
    {
        name: "forms.checkbox",
        img_url: "https://static.aot.plus/images/question-types/question-checkbox.svg",
        display_type: QuestionDisplayType.CHECKBOX,
        type: QuestionType.MULTI_SELECT
    },
    {
        name: "forms.file_upload",
        img_url: "https://static.aot.plus/images/question-types/question-file-upload.svg",
        display_type: QuestionDisplayType.FILE,
        type: QuestionType.TEXT
    }
]

export type IntakeChoiceAnswerDTO = {
    intakeAnswerId: string
    intakeClientId?: string
    intakeQuestionId: string
}

type IntakeChoiceAnswer = {
    intake_answer_id: string
    intake_client_id?: string
    intake_question_id: string
}

function mapChoiceAnswerToDTO(choice_answer: IntakeChoiceAnswer): IntakeChoiceAnswerDTO {
    return ({
        intakeAnswerId: choice_answer.intake_answer_id,
        intakeClientId: choice_answer.intake_client_id,
        intakeQuestionId: choice_answer.intake_question_id
    } as IntakeChoiceAnswerDTO)
}

export type IntakeTextAnswerDTO = {
    answer: string
    intakeClientId?: string
    intakeQuestionId: string
}

type IntakeTextAnswer = {
    answer: string
    intake_client_id?: string
    intake_question_id: string
}

function mapTextAnswerToDTO(text_answer: IntakeTextAnswer): IntakeTextAnswerDTO {
    return ({
        answer: text_answer.answer,
        intakeClientId: text_answer.intake_client_id,
        intakeQuestionId: text_answer.intake_question_id
    } as IntakeTextAnswerDTO)
}

export type IntakeResponseDTO = {
    choiceAnswers?: IntakeChoiceAnswerDTO[]
    clientId?: string
    dateTaken?: string
    id?: string
    intakeFormId: string
    orderId?: string
    textAnswers?: IntakeTextAnswerDTO[]
}

export type IntakeResponse = {
    choice_answers?: IntakeChoiceAnswer[]
    client_id?: string
    date_taken?: Date
    id?: string
    intake_form_id: string
    order_id?: string
    text_answers?: IntakeTextAnswer[]
}

export function mapIntakeResponseFromDTO(response: IntakeResponseDTO): IntakeResponse {
    return ({
        choice_answers: response.choiceAnswers?.map((choice_answer) => {
            return {
                intake_answer_id: choice_answer.intakeAnswerId,
                intake_client_id: choice_answer.intakeClientId,
                intake_question_id: choice_answer.intakeQuestionId
            } as IntakeChoiceAnswer
        }),
        client_id: response.clientId,
        date_taken: response.dateTaken ? parseISOString(response.dateTaken) : undefined,
        id: response.id,
        intake_form_id: response.intakeFormId,
        order_id: response.orderId,
        text_answers: response.textAnswers?.map((text_answer) => {
            return {
                answer: text_answer.answer,
                intake_client_id: text_answer.intakeClientId,
                intake_question_id: text_answer.intakeQuestionId
            } as IntakeTextAnswer
        }),
    } as IntakeResponse)
}

export type IntakeClientResponse = {
    client: Client
    response: IntakeResponse
}

export type FormQuestionResponse = {
    responses: string[]
    display_type: QuestionDisplayType
    question: string
    type: QuestionType
}

export function mapToFormQuestionResponse(form: IntakeForm, response: IntakeResponse): FormQuestionResponse[] {
    return form.questions?.map((question) => {
        let responses: string[]
        if (question.question_type == QuestionType.TEXT) {
            responses = response.text_answers?.filter((response) => response.intake_question_id == question.id)?.map((answer) => answer.answer) || []
        } else {
            responses = response.choice_answers?.filter((response) => response.intake_question_id == question.id)
                ?.map((answer) => question.answers
                    ?.find((a) => a.id == answer.intake_answer_id)?.answer || "") || []
        }
        return {
            responses,
            display_type: question.display_type,
            question: question.question,
            type: question.question_type
        } as FormQuestionResponse
    }) || []
}

export function mapToResponseList(form: IntakeForm, response: IntakeResponse): Record<string, IntakeAnswer[]> {
    let response_list: Record<string, IntakeAnswer[]> = {}
    form.questions?.forEach((question) => {
        if (question.id) {
            let answers: IntakeAnswer[] = []
            if (question.question_type == QuestionType.TEXT) {
               answers = response.text_answers?.filter((answer) => answer.intake_question_id == question.id)?.map((answer) => {
                return ({
                    answer: answer.answer,
                    intake_question_id: answer.intake_question_id,
                    intake_client_id: answer.intake_client_id
                } as IntakeAnswer)
               }) || []
            } else {
                answers = response.choice_answers?.filter((answer) => answer.intake_question_id == question.id)?.map((answer) => {
                    let found = question.answers?.find((a) => a.id == answer.intake_answer_id)
                    if (found) {
                        found.intake_client_id = answer.intake_client_id
                    }
                    return ( found || {} as IntakeAnswer)
                   }) || []
            }
            response_list = { ...response_list, [question.id]: answers }
        }
    })
    return response_list
}

export function mapAnswersToClientResponses(form: IntakeForm, answers: Record<string, IntakeAnswer[]>, client: Client): IntakeClientResponse {
    return ({
        client,
        response: mapAnswersToResponse(form, answers)
    } as IntakeClientResponse)
}

export function mapAnswersToResponse(form: IntakeForm, answers: Record<string, IntakeAnswer[]>): IntakeResponse {
    let choice_answers: IntakeChoiceAnswer[] = []
    let text_answers: IntakeTextAnswer[] = []
    form.questions?.forEach((question) => {
        if (question.id) {
            let ans = answers[question.id]
            if (question.question_type == QuestionType.TEXT) {
                ans?.forEach((an) => {
                    text_answers.push({
                        answer: an.answer,
                        intake_question_id: question.id! //typescript forgets too easily
                    })
                })
            } else {
                ans?.forEach((an) => {
                    if (an.id) {
                        choice_answers.push({
                            intake_answer_id: an.id,
                            intake_question_id: question.id! //typescript forgets too easily
                        })
                    }
                })
            }
        }
    })

    return ({
        choice_answers,
        intake_form_id: form.id || "",
        text_answers
    } as IntakeResponse)
}

export function mapAnswersToResponseDTO(form: IntakeForm, answers: Record<string, IntakeAnswer[]>, prev_response?: IntakeResponse): IntakeResponseDTO {
    let choice_answers: IntakeChoiceAnswerDTO[] = []
    let text_answers: IntakeTextAnswerDTO[] = []
    form.questions?.forEach((question) => {
        if (question.id) {
            let ans = answers[question.id]
            if (question.question_type == QuestionType.TEXT) {
                ans?.forEach((an) => {
                    text_answers.push({
                        answer: an.answer,
                        intakeClientId: an.intake_client_id,
                        intakeQuestionId: question.id! //typescript forgets too easily
                    })
                })
            } else {
                ans?.forEach((an) => {
                    if (an.id) {
                        choice_answers.push({
                            intakeAnswerId: an.id,
                            intakeClientId: an.intake_client_id,
                            intakeQuestionId: question.id! //typescript forgets too easily
                        })
                    }
                })
            }
        }
    })

    return ({
        id: prev_response?.id,
        clientId: prev_response?.client_id,
        orderId: prev_response?.order_id,
        choiceAnswers: choice_answers,
        intakeFormId: form.id || "",
        textAnswers: text_answers
    } as IntakeResponseDTO)
}

export function mapClientResponseToDTO(client_response: IntakeClientResponse): IntakeResponseDTO {
    return ({
        choiceAnswers: client_response.response.choice_answers?.map(mapChoiceAnswerToDTO),
        clientId: client_response.response.client_id,
        id: client_response.response.id,
        intakeFormId: client_response.response.intake_form_id,
        order_id: client_response.response.order_id,
        textAnswers: client_response.response.text_answers?.map(mapTextAnswerToDTO)
    } as IntakeResponseDTO)
}
